import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, delay, of, switchMap, throwError, timer } from 'rxjs';

import { PaymentQrRepository } from '@app/domain/ports/payment-qr.repository';
import type {
  PaymentQr,
  PaymentQrCreateInput,
  PaymentQrListFilter,
  PaymentQrPage,
  PaymentQrStatus,
} from '@app/domain/models/payments-qr/payment-qr.model';
import { PaymentQrError } from '@app/domain/models/payments-qr/payment-qr.error';

/**
 * Opciones del mock. Los specs las proveen; para QA manual se cambia el
 * provider localmente (p. ej. `{ disabled: true }` para ver el método oculto).
 */
export interface PaymentQrMockOptions {
  /** Toda operación responde `503 PAYMENTS_QR_DISABLED`. */
  readonly disabled?: boolean;
  /** Retraso simulado de red (ms). */
  readonly delayMs?: number;
  /** Reloj; solo para pruebas herméticas. */
  readonly now?: () => number;
}

export const PAYMENT_QR_MOCK_OPTIONS = new InjectionToken<PaymentQrMockOptions>('PAYMENT_QR_MOCK_OPTIONS');

const DEFAULT_DELAY_MS = 400;

/**
 * Escenarios por prefijo de la referencia (QA manual):
 * - `MOCK-REBOTE`: Pending → Reserved → Pending → Reserved → Paid.
 * - `MOCK-VENCE`: vence en 1 minuto y no avanza (el detalle dice Expired al pasar).
 * - `MOCK-CUENTA` / `MOCK-CONFIG` / `MOCK-CAMPO`: la emisión falla.
 * - `MOCK-SINLINK`: el ambiente no ofrece enlace de pago (`deeplink: null`).
 * - cualquier otra: lecturas 1–2 Pending, 3–4 Reserved, 5+ Paid.
 */
type MockScenario = 'normal' | 'bounce' | 'expire';

const NORMAL_PROGRESSION: readonly PaymentQrStatus[] = ['Pending', 'Pending', 'Reserved', 'Reserved', 'Paid'];
const BOUNCE_PROGRESSION: readonly PaymentQrStatus[] = ['Pending', 'Reserved', 'Pending', 'Reserved', 'Paid'];

interface MockQr {
  qr: PaymentQr;
  scenario: MockScenario;
  reads: number;
}

const BASE64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * `qr_string` ficticio con el largo aproximado del real (~450 caracteres) para
 * que el QR dibujado tenga una densidad parecida. Determinista por id; NO es
 * una firma ni pasa por ninguna codificación de datos reales.
 */
function fakeQrString(idQr: number): string {
  let seed = (idQr * 2654435761) >>> 0;
  let body = '';
  for (let i = 0; i < 440; i++) {
    seed = (seed * 1103515245 + 12345) >>> 0;
    body += BASE64URL[seed % BASE64URL.length];
  }
  return `polipayqr:${body}`;
}

/** Base ficticia del enlace de pago (dominio reservado `.example`: nunca resuelve a nada real). */
const MOCK_DEEPLINK_BASE = 'https://pagos.polipay.example/qr';

/**
 * Enlace de pago con la misma forma que arma el back
 * (`<PAYMENTS_QR_DEEPLINK_BASE>?qr=<qr_string>`). Lo arma el MOCK porque hace
 * de back; la app nunca lo construye.
 */
function fakeDeeplink(qrString: string): string {
  return `${MOCK_DEEPLINK_BASE}?qr=${encodeURIComponent(qrString)}`;
}

@Injectable()
export class PaymentQrMockDataSource extends PaymentQrRepository {
  private readonly options = inject(PAYMENT_QR_MOCK_OPTIONS, { optional: true }) ?? {};
  private readonly delayMs = this.options.delayMs ?? DEFAULT_DELAY_MS;
  private readonly now = this.options.now ?? (() => Date.now());

  private readonly store = new Map<number, MockQr>();
  private readonly byIdempotencyKey = new Map<string, number>();
  private nextId = 100;

  constructor() {
    super();
    this.seed();
  }

  create(input: PaymentQrCreateInput, idempotencyKey: string): Observable<PaymentQr> {
    if (this.options.disabled) return this.fail(this.disabledError());

    const existing = this.byIdempotencyKey.get(idempotencyKey);
    if (existing !== undefined) {
      const known = this.store.get(existing);
      if (known) return this.ok(known.qr);
    }

    const reference = input.reference.trim();
    const upper = reference.toUpperCase();
    if (upper.startsWith('MOCK-CUENTA')) {
      return this.fail(new PaymentQrError('requires-account', 400, 'PAYMENTS_QR_REQUIRES_ACCOUNT'));
    }
    if (upper.startsWith('MOCK-CONFIG')) {
      return this.fail(new PaymentQrError('not-configured', 400, 'PAYMENTS_QR_NOT_CONFIGURED'));
    }
    if (upper.startsWith('MOCK-CAMPO')) {
      return this.fail(new PaymentQrError('validation', 400, null, ['reference']));
    }

    const scenario: MockScenario = upper.startsWith('MOCK-REBOTE')
      ? 'bounce'
      : upper.startsWith('MOCK-VENCE') ? 'expire' : 'normal';
    const minutes = scenario === 'expire' ? 1 : input.expiresInMinutes;
    const createdMs = this.now();
    const idQr = this.nextId++;
    const qrString = fakeQrString(idQr);
    const qr: PaymentQr = {
      idQr,
      gatewayId: `mock-gw-${idQr}`,
      qrString,
      status: 'Pending',
      amount: Number(input.amount),
      currency: 'MXN',
      reference,
      description: (input.description ?? '').trim(),
      idCompany: input.idCompany ?? null,
      companyName: 'Comercio Demo',
      createdByLabel: 'operador@demo.mx',
      expiresAt: new Date(createdMs + minutes * 60_000).toISOString(),
      paidAt: null,
      createdAt: new Date(createdMs).toISOString(),
      deeplink: upper.startsWith('MOCK-SINLINK') ? null : fakeDeeplink(qrString),
    };
    this.store.set(idQr, { qr, scenario, reads: 0 });
    this.byIdempotencyKey.set(idempotencyKey, idQr);
    return this.ok(qr);
  }

  list(filter: PaymentQrListFilter): Observable<PaymentQrPage> {
    if (this.options.disabled) return this.fail(this.disabledError());

    const search = (filter.search ?? '').trim().toLowerCase();
    const all = [...this.store.values()]
      .map((entry) => entry.qr)
      .filter((qr) => !filter.status || qr.status === filter.status)
      .filter((qr) => !search || [qr.reference, qr.description, qr.companyName, qr.createdByLabel, String(qr.idQr)]
        .some((field) => field.toLowerCase().includes(search)))
      .sort((a, b) => b.idQr - a.idQr);
    const offset = filter.offset ?? 0;
    const limit = filter.limit ?? 50;
    return this.ok({ count: all.length, results: all.slice(offset, offset + limit) });
  }

  getById(idQr: number): Observable<PaymentQr> {
    if (this.options.disabled) return this.fail(this.disabledError());
    const entry = this.store.get(idQr);
    if (!entry) return this.fail(new PaymentQrError('not-found', 404));
    this.advance(entry);
    return this.ok(entry.qr);
  }

  cancel(idQr: number): Observable<PaymentQr> {
    if (this.options.disabled) return this.fail(this.disabledError());
    const entry = this.store.get(idQr);
    if (!entry) return this.fail(new PaymentQrError('not-found', 404));
    // Idempotente: cancelar dos veces devuelve el mismo QR.
    if (entry.qr.status === 'Cancelled') return this.ok(entry.qr);
    if (entry.qr.status !== 'Pending') {
      return this.fail(new PaymentQrError('conflict', 409, 'UPSTREAM_REJECTED'));
    }
    entry.qr = { ...entry.qr, status: 'Cancelled' };
    return this.ok(entry.qr);
  }

  /** Cada lectura del detalle avanza la máquina de estados (el detalle reconcilia). */
  private advance(entry: MockQr): void {
    const current = entry.qr.status;
    if (current === 'Paid' || current === 'Cancelled' || current === 'Expired') return;

    const nowMs = this.now();
    const expired = entry.qr.expiresAt !== null && Date.parse(entry.qr.expiresAt) < nowMs;
    if (entry.scenario === 'expire' || (expired && current === 'Pending')) {
      if (expired) entry.qr = { ...entry.qr, status: 'Expired' };
      return;
    }

    entry.reads += 1;
    const progression = entry.scenario === 'bounce' ? BOUNCE_PROGRESSION : NORMAL_PROGRESSION;
    const next = progression[Math.min(entry.reads, progression.length) - 1];
    entry.qr = next === 'Paid'
      ? { ...entry.qr, status: 'Paid', paidAt: new Date(nowMs).toISOString() }
      : { ...entry.qr, status: next };
  }

  private ok<T>(value: T): Observable<T> {
    return of(value).pipe(delay(this.delayMs));
  }

  private fail<T>(error: PaymentQrError): Observable<T> {
    return timer(this.delayMs).pipe(switchMap(() => throwError(() => error)));
  }

  private disabledError(): PaymentQrError {
    return new PaymentQrError('disabled', 503, 'PAYMENTS_QR_DISABLED');
  }

  /** Historial para que el listado no arranque vacío (incluye un Pending ya vencido). */
  private seed(): void {
    const nowMs = this.now();
    const minute = 60_000;
    // [estado, referencia, monto, vence en (min), ¿trae enlace de pago?]
    const rows: ReadonlyArray<[PaymentQrStatus, string, number, number, boolean]> = [
      ['Paid', 'Ticket 4815', 150, -180, true],
      ['Pending', 'Ticket 4816', 89.9, -45, true],
      ['Cancelled', 'Ticket 4817', 1200, -30, true],
      ['Expired', 'Ticket 4818', 35.5, -25, true],
      ['Reserved', 'Ticket 4819', 420, 5, true],
      // Sin enlace: así QA ve también la ventana sin los botones de copiar/compartir.
      ['Pending', 'Ticket 4820', 75, 12, false],
    ];
    rows.forEach(([status, reference, amount, expiresInMin, withDeeplink], index) => {
      const idQr = index + 1;
      const createdMs = nowMs + (expiresInMin - 15) * minute;
      const qrString = fakeQrString(idQr);
      const qr: PaymentQr = {
        idQr,
        gatewayId: `mock-gw-${idQr}`,
        qrString,
        status,
        amount,
        currency: 'MXN',
        reference,
        description: index % 2 === 0 ? 'Mesa 7' : '',
        idCompany: null,
        companyName: 'Comercio Demo',
        createdByLabel: 'operador@demo.mx',
        expiresAt: new Date(nowMs + expiresInMin * minute).toISOString(),
        paidAt: status === 'Paid' ? new Date(createdMs + 3 * minute).toISOString() : null,
        createdAt: new Date(createdMs).toISOString(),
        deeplink: withDeeplink ? fakeDeeplink(qrString) : null,
      };
      // Los sembrados no avanzan solos: son historial (el Reserved sí completa
      // la progresión normal si alguien abre su detalle).
      this.store.set(idQr, { qr, scenario: 'normal', reads: status === 'Reserved' ? 2 : 0 });
    });
  }
}
